import React from "react";
import { DashProfitLossSidebarSkeleton } from "./DashCardSkeletons";
import { getAmount } from "../../../../utils/helper";

function formatCompactAmount(value, currencySign) {
  const amount = Number(value) || 0;
  const sign = amount < 0 ? "-" : "";
  return getAmount(Math.abs(amount), currencySign, { decimals: 2 });
}

function formatPlainNumber(value) {
  const n = Number(value) || 0;
  const sign = n < 0 ? "-" : "";
  return `${sign}${Math.abs(Math.round(n)).toLocaleString()}`;
}

function formatRowAmount(row, currencySign) {
  if (row.amountFormat === "number") return formatPlainNumber(row.amount);
  return getAmount(Number(row.amount) || 0, currencySign, { decimals: 2 });
}

function formatChildValue(child, currencySign) {
  if (child.type === "number") return formatPlainNumber(child.value);
  return getAmount(Number(child.value) || 0, currencySign, { decimals: 2 });
}

function formatTrendPct(pct) {
  if (pct == null || Number.isNaN(Number(pct))) return null;
  const n = Number(pct);
  const arrow = n > 0 ? "↑" : n < 0 ? "↓" : "→";
  return `${arrow}${Math.abs(n).toFixed(2)}%`;
}

function CompareLine({ compare, currencySign, compact = false, amountFormat = "number" }) {
  if (!compare) return null;
  const pct = formatTrendPct(compare.pct);
  const deltaText =
    amountFormat === "number"
      ? formatPlainNumber(compare.delta)
      : getAmount(compare.delta, currencySign, { decimals: 2 });
  
  // 🟢 Green for UP (positive), 🔴 Red for DOWN or neutral
  const isPctPositive = compare.pct > 0;

  return (
    <div className="pl-sidebar__compare">
      <span >
        {deltaText}
      </span>
      {pct ? (
        <span
          className={`pl-sidebar__compare-pct ${isPctPositive ? "pl-sidebar__compare-pct--up" : "pl-sidebar__compare-pct--down"}`}
        >
          {pct}
        </span>
      ) : null}
    </div>
  );
}

function RowAmounts({ row, currencySign }) {
  return (
    <div className="pl-sidebar__row-amounts">
      <div className="pl-sidebar__amount" >
        {formatRowAmount(row, currencySign)}
      </div>
      {row.kind === "gross" && row.margin != null ? (
        <div className="pl-sidebar__margin">{Number(row.margin).toFixed(2)}% margin</div>
      ) : (
        <CompareLine compare={row.compare} currencySign={currencySign} compact amountFormat={row.amountFormat} />
      )}
    </div>
  );
}

export default function DashProfitLossSidebarCard({ loading, profitLoss, currencySign = "₹" }) {
  const rows = profitLoss?.rows || [];
  const net = profitLoss?.netProfit || {};
  const isNetPositive = Number(net.amount) > 0;

  return (
    <div className="pl-sidebar card border border-gray-200 shadow-none  overflow-hidden">
      {loading ? (
        <DashProfitLossSidebarSkeleton />
      ) : (
        <>
          <div className="pl-sidebar__head">
            <div>
              <h3 className="pl-sidebar__title fw-bolder mb-0 fs-5">Profit &amp; Loss</h3>
              <div className="pl-sidebar__subtitle">Expand rows to see breakdown</div>
            </div>
          </div>

          <div className="pl-sidebar__scroll">
            <div className="pl-sidebar__rows">
              {rows.map((row) => {
                const expandable = !!(row.children && row.children.length);

                if (row.kind === "gross") {
                  const grossAmount = Number(row.amount) || 0;
                  const isGrossPositive = grossAmount > 0;
                  return (
                    <div
                      key={row.key}
                      className={`pl-sidebar__row pl-sidebar__row--gross ${
                        isGrossPositive ? "pl-sidebar__row--gross-positive" : "pl-sidebar__row--gross-negative"
                      }`}
                    >
                      <div className="pl-sidebar__row-main">
                        <div className="pl-sidebar__row-title">{row.label}</div>
                        <div className="pl-sidebar__row-subtitle">{row.subtitle}</div>
                      </div>
                      <RowAmounts row={row} currencySign={currencySign} />
                    </div>
                  );
                }

                if (expandable) {
                  return (
                    <div key={row.key} className={`pl-sidebar__row pl-sidebar__row--${row.kind} pl-sidebar__row--expandable`}>
                      <details className="pl-sidebar__details">
                        <summary className="pl-sidebar__details-summary">
                          <span className="pl-sidebar__chev" aria-hidden>
                            <i className="ki-outline ki-right" style={{ fontSize: "1rem" }} />
                          </span>
                          <span className="pl-sidebar__dash" aria-hidden />
                          <div className="pl-sidebar__row-main">
                            <div className="pl-sidebar__row-title">{row.label}</div>
                            <div className="pl-sidebar__row-subtitle">{row.subtitle}</div>
                          </div>
                          <RowAmounts row={row} currencySign={currencySign} />
                        </summary>
                        <div className="pl-sidebar__children" role="region" aria-label={`${row.label} breakdown`}>
                          {row.children.map((c) => (
                            <div key={c.key} className="pl-sidebar__child">
                              <span className="pl-sidebar__child-title">{c.title}</span>
                              <span className="pl-sidebar__child-value">{formatChildValue(c, currencySign)}</span>
                            </div>
                          ))}
                        </div>
                      </details>
                    </div>
                  );
                }

                return (
                  <div key={row.key} className={`pl-sidebar__row pl-sidebar__row--${row.kind}`}>
                    <span className="pl-sidebar__chev" aria-hidden>›</span>
                    <span className="pl-sidebar__dash" aria-hidden />
                    <div className="pl-sidebar__row-main">
                      <div className="pl-sidebar__row-title">{row.label}</div>
                      <div className="pl-sidebar__row-subtitle">{row.subtitle}</div>
                    </div>
                    <RowAmounts row={row} currencySign={currencySign} />
                  </div>
                );
              })}
            </div>

            <div className={`pl-sidebar__net ${
                isNetPositive ? "pl-sidebar__net--positive" : "pl-sidebar__net--negative"
              }`}>
              <div className="d-flex align-items-start justify-content-between gap-3">
                <div>
                  <div className="pl-sidebar__net-label">= {net.label || "Net Profit"}</div>
                  <div className="pl-sidebar__net-subtitle">Your take-home</div>
                </div>
                <div className="text-end">
                  <div className="pl-sidebar__net-amount">
                    {getAmount(Number(net.amount) || 0, currencySign, { decimals: 2 })}
                  </div>
                  <div className="pl-sidebar__net-margin">
                    {Number(net.margin || 0).toFixed(2)}% margin · {getAmount(Number(net.payout) || 0, currencySign, { decimals: 2 })}
                  </div>
                </div>
              </div>
              {net.compare ? (
                <div className="pl-sidebar__net-compare">
                  <span>{profitLoss?.compareLabel || "vs prior period"}</span>
                  <span >
                    {getAmount(Number(net.compare.delta) || 0, currencySign, { decimals: 2 })}
                  </span>
                  {formatTrendPct(net.compare.pct) ? (
                    <span
                      className={`pl-sidebar__compare-pct ${
                        Number(net.compare.pct) > 0
                          ? "pl-sidebar__compare-pct--up"
                          : "pl-sidebar__compare-pct--down"
                      }`}
                    >
                      {formatTrendPct(net.compare.pct)}
                    </span>
                  ) : null}
                </div>
              ) : null}
            </div>
          </div>
        </>
      )}
    </div>
  );
}